'use strict';
const goldAsset = require('./gold-asset'),
	isUploadableImage = require('@mindmup/mindmup-common/src/is-uploadable-image'),
	flattenIdeas = require('@mindmup/mapjs-core/src/content/flatten-ideas'),
	CMD_FLAG = 'gold-resource-manager:asset';

module.exports = function GoldResourceManager(s3FileModel, dimensionCalculator, pageState, collaborationModel) {
	let signedAssetUrlMap = {};
	const self = this,
		prefix = 'internal:',
		prefixMatcher = new RegExp('^' + prefix),
		joinAndSort = function () {
			const arrays = Array.from(arguments),
				joined = new Set();
			arrays.forEach(array => {
				if (array) {
					array.forEach (element => {
						if (element) {
							joined.add(element);
						}
					});
				}
			});
			return Array.from(joined).sort();
		},
		initializeAssetUrls = function (additionalIds) {

			const currentAssetIds = (signedAssetUrlMap && Object.keys(signedAssetUrlMap).map(goldAsset.assetIdFromResourceId)) || [],
				activeContent = collaborationModel.getActiveContent(),
				assetIdsFromContent = activeContent && goldAsset.assetIdsFromContent(activeContent),
				assetIds = joinAndSort(assetIdsFromContent, additionalIds, currentAssetIds),
				fileId = pageState.getFileId();

			if (!activeContent) {
				throw 'not-initialized';
			}

			if (assetIds.length) {
				return s3FileModel.getAssetUrls(fileId, assetIds, pageState.getPathInfo())
				.then(assetIdToUrlMap => goldAsset.resourceIdToUrlMap(assetIdToUrlMap))
				.then(function (result) {
					signedAssetUrlMap = result;
				});
			} else {
				signedAssetUrlMap = {};
			}
			return Promise.resolve({});
		},
		ensureGoldAssetIds = (assetIds) => {
			if (!assetIds || !assetIds.length) {
				return Promise.resolve([]);
			}
			const missing = assetIds.filter(assetId => assetId && !signedAssetUrlMap[goldAsset.resourceIdFromAssetId(assetId)]);
			if (!missing.length) {
				return Promise.resolve([]);
			}
			return s3FileModel.getAssetUrls(pageState.getFileId(), missing, pageState.getPathInfo())
			.then(assetIdToUrlMap => goldAsset.resourceIdToUrlMap(assetIdToUrlMap))
			.then(result => {
				Object.keys(result).forEach(key => (signedAssetUrlMap[key] = result[key]));
				return missing;
			});
		},
		createResourceEvent = function (assetId) {
			return {cmd: CMD_FLAG, args: [assetId]};
		},
		isResourceEvent = function (event) {
			return event && event.cmd === CMD_FLAG;
		};

	self.resignUrls = function (events) {
		const resourceEvents = events && events.filter(isResourceEvent),
			assetIdsFromEvents = resourceEvents && resourceEvents.map(evt => evt.args).reduce((acc, curr) => acc.concat(curr), []).filter(f => f);
		return initializeAssetUrls(assetIdsFromEvents);
	};
	self.processAssetEvents = function (envelopes) {
		if (!envelopes || envelopes.length === 0) {
			return Promise.resolve(envelopes);
		}
		let assetIds = [];
		envelopes.forEach(envelope => {
			const contentEvents = [];
			if (envelope.events && envelope.events.length) {
				envelope.events.forEach(e => {
					if (isResourceEvent(e)) {
						assetIds = assetIds.concat(e.args);
					} else {
						contentEvents.push(e);
					}
				});
			};
			envelope.events = contentEvents;
		});
		return ensureGoldAssetIds(assetIds).then(() => envelopes);
	};
	self.storeResource = function (resourceURL) {
		const activeContent = collaborationModel.getActiveContent();
		if (!activeContent) {
			throw 'not-initialized';
		}
		if (!isUploadableImage(resourceURL)) {
			return resourceURL;
		}
		return prefix + activeContent.storeResource(resourceURL);
	};
	self.addGoldAsset = function (dataUri) {
		const fileId = pageState.getFileId();

		return s3FileModel.uploadAsset(fileId, dataUri)
			.then(assetId => {
				const resourceId = goldAsset.resourceIdFromAssetId(assetId);
				return ensureGoldAssetIds([assetId]).then(() => {
					collaborationModel.dispatchLocalContentEvent(createResourceEvent(assetId));
					return resourceId;
				});
			});
	};
	self.hasUploadableAsset = function (idea) {
		return pageState.getAllowedActions().uploadChanges &&
			(goldAsset.getUploadableImage(idea) || goldAsset.getUploadableAttachment(idea));
	};
	self.uploadAsset = function (idea) {
		const fileId = pageState.getFileId();
		return goldAsset.uploadAssets(fileId, idea, s3FileModel)
			.then(ensureGoldAssetIds)
			.then(assetIds => assetIds.forEach(assetId => collaborationModel.dispatchLocalContentEvent(createResourceEvent(assetId))));
	};

	self.uploadAssetsAsChanges = async (onProgress, noop) => {
		const flattened = flattenIdeas(collaborationModel.getActiveContent()).filter(self.hasUploadableAsset),
			uploadAttachmentAsset = async (idea) => {
				const attachment = goldAsset.getUploadableAttachment(idea),
					resourceId  = attachment && await self.addGoldAsset(attachment.content);
				goldAsset.replaceAttachmentWithResourceId(collaborationModel.getActiveContent(), idea.id, resourceId, attachment);
			},
			uploadImageAsset = async (idea) => {
				const image = goldAsset.getUploadableImage(idea),
					resourceId  = image && await self.addGoldAsset(image.url);
				goldAsset.replaceImageWithResourceId(collaborationModel.getActiveContent(), idea.id, resourceId);
			};
		if (!flattened.length) {
			noop();
			return;
		}
		let remaining = flattened.length;

		onProgress(flattened.length);
		for (const idea of flattened) {
			await uploadImageAsset(idea);
			await uploadAttachmentAsset(idea);
			remaining -= 1;
			onProgress(remaining);
		}
	};
	self.getResource = function (resourceURL) {
		const activeContent = collaborationModel.getActiveContent(),
			goldAssetUrl = signedAssetUrlMap && signedAssetUrlMap[resourceURL];

		if (!activeContent || !signedAssetUrlMap) {
			throw 'not-initialized';
		}
		if (goldAssetUrl) {
			return goldAssetUrl;
		}
		if (prefixMatcher.test(resourceURL)) {
			return activeContent.getResource(resourceURL.substring(prefix.length));
		} else {
			return resourceURL;
		}
	};
	self.calculateImageDimensions = function (resourceUrl) {
		const unpackedUrl = self.getResource(resourceUrl);
		return dimensionCalculator(unpackedUrl);
	};
};
